import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { ProjectsService } from '../../common/services';
import { CatalogsService } from '../../common/services/catalogs.service';
import { useLocation } from 'react-router';
import { Button, Snackbar } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useStylesGeneral } from '../../styles/StyleComponents';
import "../../styles/site.css";
import TextTitleCS from '../../common/ComponentesCS/TextTitleCS';
import StepperCS from '../../common/ComponentesCS/StepperCS';
import ModalCS from '../../common/ComponentesCS/ModalCS';
import ProjectDetails from './steps/ProjectDetails';
import ProductDetails from './steps/ProductDetails';
import ProductSeries from './steps/ProductSeries';
import ProjectOrderPreview from './steps/ProjectOrderPreview';
import { INTELISIS_ACCOUNT_FIELDS, REQUIRED_INTELISIS_ACCOUNT_FIELDS } from './constants/intelisisConfig';
import { getSeriesValue, normalizeProjectCompleteDetail } from './utils/projectCompleteDetail';

const normalizeIntelisisOptions = (source) => {
    if (Array.isArray(source)) {
        return source;
    }

    if (Array.isArray(source?.data)) {
        return source.data;
    }

    return [];
};

const hasValue = (value) => value !== undefined && value !== null && `${value}`.trim() !== '';
const getFirstValue = (...values) => values.find((value) => hasValue(value)) ?? null;
const MAX_ARTICLE_CODE_LENGTH = 20;
const isWithinMaxLength = (value, maxLength) => !hasValue(value) || `${value}`.length <= maxLength;
const isValidArticleCode = (value) => /^[A-Za-z0-9]+$/.test(`${value ?? ''}`);
const requiresIntelisisConfig = (product) => product?.altaIntelisis !== true;
const PROJECT_SELECTION_STEP_INDEX = 0;
const PRODUCT_DETAILS_STEP_INDEX = 1;
const PRODUCT_SERIES_STEP_INDEX = 2;
const PROJECT_ORDER_PREVIEW_STEP_INDEX = 3;
const PROJECT_ORDER_GENERATED_STEP_INDEX = 4;
const STEP_CHANGE_CONFIRMATION_STEPS = [
    PRODUCT_DETAILS_STEP_INDEX,
    PRODUCT_SERIES_STEP_INDEX,
    PROJECT_ORDER_PREVIEW_STEP_INDEX,
    PROJECT_ORDER_GENERATED_STEP_INDEX
];

const normalizeNumber = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
};

const isValidNumericValue = (value) => hasValue(value) && Number.isFinite(Number(value));
const resolveEffectiveCantidad = (product) => {
    const pending = product?.cantidadPendiente;
    const effective = (pending !== null && pending !== undefined) ? pending : product?.cantidad;
    const numeric = Number(effective);
    return Number.isFinite(numeric) ? numeric : 0;
};
const getMissingFields = (payload, requiredFields) => requiredFields.filter((field) => !hasValue(payload?.[field]));
const normalizeComparableValue = (value) => `${value ?? ''}`.trim().toLowerCase();
const normalizeResponseKey = (value) => normalizeComparableValue(value).replace(/[^a-z0-9]/g, '');
const normalizeBooleanValue = (value) => {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'number') {
        return value === 1;
    }

    const normalizedValue = `${value ?? ''}`.trim().toLowerCase();

    return ['true', '1', 'si', 'yes'].includes(normalizedValue);
};

const TOP_INFO_ART_FIELDS = [
    { responseKeys: ['descripcion1', 'Descripcion1'], productKey: 'descripcion1' },
    { responseKeys: ['nombreCorto', 'NombreCorto', 'nombrecorto'], productKey: 'nombreCorto' },
    { responseKeys: ['proveedor', 'Proveedor'], productKey: 'proveedor' },
    { responseKeys: ['unidad', 'Unidad'], productKey: 'unidad' },
    { responseKeys: ['unidadCompra', 'UnidadCompra'], productKey: 'unidadCompra' },
    { responseKeys: ['familia', 'Familia'], productKey: 'familia' },
    { responseKeys: ['monedaPrecio', 'MonedaPrecio'], productKey: 'monedaPrecio' },
    { responseKeys: ['rama', 'Rama'], productKey: 'rama' },
    { responseKeys: ['csmktCategoria', 'CSMKTCategoria', 'CsmktCategoria'], productKey: 'csmktCategoria' },
    { responseKeys: ['impuesto1', 'Impuesto1'], productKey: 'impuesto1' },
    { responseKeys: ['impuesto1Excento', 'Impuesto1Excento', 'impuesto1Exento', 'Impuesto1Exento'], productKey: 'impuesto1Excento' },
    { responseKeys: ['fiscalIngresoTercero', 'FiscalIngresoTercero'], productKey: 'fiscalIngresoTercero' },
    { responseKeys: ['seVende', 'SeVende'], productKey: 'seVende' },
    { responseKeys: ['seCompra', 'SeCompra'], productKey: 'seCompra' },
    { responseKeys: ['cuenta', 'Cuenta'], productKey: 'intelisis_cuenta' },
    { responseKeys: ['cuenta2', 'Cuenta2'], productKey: 'intelisis_cuenta2' },
    { responseKeys: ['cuenta3', 'Cuenta3'], productKey: 'intelisis_cuenta3' },
    { responseKeys: ['cuenta4', 'Cuenta4'], productKey: 'intelisis_cuenta4' },
    { responseKeys: ['cuenta5', 'Cuenta5'], productKey: 'intelisis_cuenta5' },
    { responseKeys: ['retencion1Concepto', 'Retencion1Concepto'], productKey: 'intelisis_reteFuente' },
    { responseKeys: ['retencion3Concepto', 'Retencion3Concepto'], productKey: 'intelisis_reteICA' },
    { responseKeys: ['csProcesador', 'CsProcesador'], productKey: 'csProcesador' },
    { responseKeys: ['csSistemaOperativo', 'CsSistemaOperativo'], productKey: 'csSistemaOperativo' }
];

const REQUIRED_INTELISIS_PRODUCT_FIELDS = [
    { field: 'descripcion1', valueKeys: ['descripcion1', 'Descripcion1', 'descripcion', 'Descripcion'] },
    { field: 'nombreCorto', valueKeys: ['nombreCorto', 'NombreCorto', 'nombrecorto', 'articulo'] },
    { field: 'fabricante', valueKeys: ['fabricante'] },
    { field: 'categoria', valueKeys: ['categoria'] },
    { field: 'proveedor', valueKeys: ['proveedor', 'Proveedor'] },
    { field: 'unidad', valueKeys: ['unidad'] },
    { field: 'unidadCompra', valueKeys: ['unidadCompra'] },
    { field: 'familia', valueKeys: ['familia'] },
    { field: 'monedaPrecio', valueKeys: ['monedaPrecio'] },
    { field: 'rama', valueKeys: ['rama'] },
    { field: 'csmktCategoria', valueKeys: ['csmktCategoria', 'CSMKTCategoria'] },
    { field: 'impuesto1', valueKeys: ['impuesto1', 'Impuesto1'] },
    { field: 'impuesto1Excento', valueKeys: ['impuesto1Excento', 'Impuesto1Excento', 'impuesto1Exento', 'Impuesto1Exento'] }
];

const buildTopInfoArtKey = (fabricante, categoria) => {
    const normalizedFabricante = normalizeComparableValue(fabricante);
    const normalizedCategoria = normalizeComparableValue(categoria);

    if (!normalizedFabricante || !normalizedCategoria) {
        return '';
    }

    return `${normalizedFabricante}|${normalizedCategoria}`;
};

const normalizeTopInfoArtResponse = (response) => {
    if (Array.isArray(response)) {
        return response[0] || {};
    }

    if (Array.isArray(response?.data)) {
        return response.data[0] || {};
    }

    if (response?.data && typeof response.data === 'object') {
        return response.data;
    }

    return response || {};
};

const getTopInfoArtValue = (topInfoArtData, responseKeys = []) => {
    const responseKey = responseKeys.find((key) => hasValue(topInfoArtData?.[key]));

    if (responseKey) {
        return topInfoArtData[responseKey];
    }

    const normalizedResponseKeys = responseKeys.map(normalizeResponseKey);
    const matchingEntry = Object.entries(topInfoArtData || {}).find(([key, value]) => (
        normalizedResponseKeys.includes(normalizeResponseKey(key))
        && hasValue(value)
    ));

    return matchingEntry ? matchingEntry[1] : undefined;
};

const getStepChangeConfirmationCopy = (toStep) => {
    if (toStep === PRODUCT_DETAILS_STEP_INDEX) {
        return {
            title: 'Confirmar seleccion de facturas',
            message: 'Verifica que las facturas y articulos seleccionados sean correctos. Una vez que continues podras ajustar su configuracion.'
        };
    }

    if (toStep === PRODUCT_SERIES_STEP_INDEX) {
        return {
            title: 'Procesar articulos y precios',
            message: 'Al continuar se procesaran los articulos y se registraran los precios correspondientes. A partir de este punto no podras modificar la seleccion anterior.'
        };
    }

    if (toStep === PROJECT_ORDER_PREVIEW_STEP_INDEX) {
        return {
            title: 'Confirmar asignacion de series',
            message: 'Al continuar se validaran las series asignadas y se confirmara la orden del proyecto.'
        };
    }

    if (toStep === PROJECT_ORDER_GENERATED_STEP_INDEX) {
        return {
            title: 'Generar orden de compra',
            message: 'Al continuar se finalizara el proyecto y se generara el resumen de la orden. Esta accion no se puede revertir.'
        };
    }

    return {
        title: 'Confirmar accion',
        message: '¿Deseas continuar?'
    };
};

const mergeTopInfoArtIntoProduct = (product, topInfoArtData) => {
    if (!product || !topInfoArtData) {
        return product;
    }

    // Se marca como cargado aunque no haya campos nuevos, para no repetir la misma consulta.
    let hasChanges = product?.topInfoArtLoaded !== true;
    const nextProduct = { ...product };

    // TopInfoArt solo completa campos vacios; cualquier dato capturado por el usuario se conserva.
    TOP_INFO_ART_FIELDS.forEach(({ responseKeys, productKey }) => {
        const currentValue = product?.[productKey];
        const suggestedValue = getTopInfoArtValue(topInfoArtData, responseKeys);

        if (hasValue(currentValue) || !hasValue(suggestedValue)) {
            return;
        }

        nextProduct[productKey] = suggestedValue;
        hasChanges = true;
    });

    nextProduct.topInfoArtLoaded = true;

    // Si no hubo ningun cambio real, se devuelve la misma referencia para evitar renders innecesarios.
    return hasChanges ? nextProduct : product;
};

const getDuplicateIntelisisAccountFields = (product, fieldKeyName = 'field') => {
    const seenValues = new Map();
    const duplicateFields = new Set();

    INTELISIS_ACCOUNT_FIELDS.forEach((accountField) => {
        const normalizedValue = normalizeComparableValue(product?.[accountField.valueKey]);

        if (!normalizedValue) {
            return;
        }

        if (seenValues.has(normalizedValue)) {
            duplicateFields.add(accountField[fieldKeyName]);
            duplicateFields.add(seenValues.get(normalizedValue));
            return;
        }

        seenValues.set(normalizedValue, accountField[fieldKeyName]);
    });

    return Array.from(duplicateFields);
};

const buildRegistrarArtPayload = (product, projectData) => {
    const descripcion1 = getFirstValue(
        product?.descripcion1,
        product?.Descripcion1,
        product?.descripcion,
        product?.Descripcion,
        product?.articulo
    );
    const nombreCorto = getFirstValue(
        product?.nombreCorto,
        product?.NombreCorto,
        product?.nombrecorto,
        product?.articulo
    );
    const impuesto1Excento = getFirstValue(
        product?.impuesto1Excento,
        product?.Impuesto1Excento,
        product?.impuesto1Exento,
        product?.Impuesto1Exento
    );
    const fiscalIngresoTercero = getFirstValue(
        product?.fiscalIngresoTercero,
        product?.FiscalIngresoTercero
    );
    const seVende = getFirstValue(
        product?.seVende,
        product?.SeVende
    );
    const seCompra = getFirstValue(
        product?.seCompra,
        product?.SeCompra
    );

    const categoriaValue = product?.categoria ?? null;
    const familiaValue = product?.familia ?? null;

    const payload = {
        IdProyecto: projectData?.idProyecto ?? null,
        IdProyectoD: projectData?.idProyectoD ?? null,
        IdFactura: product?.idFactura ?? null,
        IdArticulo: product?.idArticulo ?? null,
        Articulo: product?.articulo ?? null,
        Descripcion1: descripcion1,
        NombreCorto: nombreCorto,
        Cantidad: normalizeNumber(product?.cantidad),
        PrecioLista: normalizeNumber(product?.precioUnitario ?? product?.precio),
        Unidad: product?.unidad ?? null,
        UnidadCompra: product?.unidadCompra ?? null,
        Categoria: categoriaValue,
        Familia: familiaValue,
        Fabricante: product?.fabricante ?? null,
        Proveedor: product?.proveedor ?? product?.Proveedor ?? projectData?.proveedor ?? projectData?.Proveedor ?? null,
        MonedaPrecio: product?.monedaPrecio ?? projectData?.moneda ?? null,
        Rama: product?.rama ?? null,
        CSMKTCategoria: product?.CSMKTCategoria ?? product?.csmktCategoria ?? null,
        Impuesto1: isValidNumericValue(product?.impuesto1 ?? product?.Impuesto1)
            ? normalizeNumber(product?.impuesto1 ?? product?.Impuesto1)
            : null,
        Impuesto1Excento: hasValue(impuesto1Excento) ? normalizeBooleanValue(impuesto1Excento) : null,
        FiscalIngresoPropio: !(hasValue(fiscalIngresoTercero) && normalizeBooleanValue(fiscalIngresoTercero)),
        FiscalIngresoTercero: hasValue(fiscalIngresoTercero) ? normalizeBooleanValue(fiscalIngresoTercero) : false,
        SeVende: hasValue(seVende) ? normalizeBooleanValue(seVende) : true,
        SeCompra: hasValue(seCompra) ? normalizeBooleanValue(seCompra) : true,
        Retencion1Concepto: product?.intelisis_reteFuente ?? null,
        Retencion3Concepto: product?.intelisis_reteICA ?? null,
        AltaIntelisis: product?.altaIntelisis ?? null,
        Factura: product?.factura ?? null,
        csProcesador: product?.csProcesador ?? null,
        csSistemaOperativo: product?.csSistemaOperativo ?? null
    };

    INTELISIS_ACCOUNT_FIELDS.forEach(({ valueKey, payloadKey }) => {
        payload[payloadKey] = product?.[valueKey] ?? null;
    });

    return {
        payload,
        missingFields: Array.from(new Set([
            ...getMissingFields(payload, [
                'IdArticulo',
                'Articulo',
                'Descripcion1',
                'NombreCorto',
                'Unidad',
                'UnidadCompra',
                'Categoria',
                'Familia',
                'Fabricante',
                'Proveedor',
                'MonedaPrecio',
                'Rama',
                'Impuesto1',
                'Impuesto1Excento',
                ...REQUIRED_INTELISIS_ACCOUNT_FIELDS.map(({ payloadKey }) => payloadKey),
                'Retencion1Concepto',
                'Retencion3Concepto',
                'CSMKTCategoria'
            ]),
            ...(!isValidArticleCode(payload.Articulo) ? ['Articulo'] : []),
            ...(!isWithinMaxLength(payload.Articulo, MAX_ARTICLE_CODE_LENGTH) ? ['Articulo'] : []),
            ...(!isWithinMaxLength(payload.NombreCorto, MAX_ARTICLE_CODE_LENGTH) ? ['NombreCorto'] : []),
            ...(!isValidNumericValue(product?.impuesto1 ?? product?.Impuesto1) ? ['Impuesto1'] : []),
            ...getDuplicateIntelisisAccountFields(product, 'payloadKey')
        ]))
    };
};

const buildInsertPricePayload = (product, projectData) => ({
    IdProyecto: projectData?.idProyecto ?? null,
    IdProyectoD: projectData?.idProyectoD ?? null,
    IdArticulo: product?.idArticulo ?? null,
    Precio: normalizeNumber(product?.precioUnitario),
    Articulo: product?.articulo ?? null,
    CantidadSeleccionada: normalizeNumber(product?.cantidadSeleccionada),
    Description: product?.descripcion ?? null,
    EstatusIntelisis: product?.estatusIntelisis ?? null
});

const getProductSeriesValidationSummary = (productsSelected = {}, productSeries = {}) => {
    const invalidProducts = [];

    Object.entries(productsSelected || {}).forEach(([idFactura, products]) => {
        (products || []).forEach((product) => {
            const requiredQuantity = normalizeNumber(product?.cantidad);

            if (requiredQuantity <= 0) {
                return;
            }

            const productKey = `${product?.idFactura ?? idFactura}-${product?.idArticulo}`;
            const assignedSeries = Array.isArray(productSeries?.[productKey]) ? productSeries[productKey] : [];

            if (assignedSeries.length !== requiredQuantity) {
                invalidProducts.push({
                    productKey,
                    idFactura: product?.idFactura ?? idFactura,
                    idArticulo: product?.idArticulo,
                    articulo: product?.articulo,
                    requiredQuantity,
                    assignedSeriesCount: assignedSeries.length
                });
            }
        });
    });

    return {
        invalidProducts,
        invalidCount: invalidProducts.length
    };
};

const buildConfirmProjectPayload = (productsSelected, projectData) => {
    const facturas = Object.entries(productsSelected || {}).reduce((payloads, [idFactura, products]) => {
        const selectedArticleIds = Array.from(new Set(
            (products || [])
                .map((product) => product?.idArticulo)
                .filter((idArticulo) => hasValue(idArticulo))
        ));

        if (selectedArticleIds.length === 0) {
            return payloads;
        }

        const firstProduct = (products || []).find((product) => hasValue(product?.idFactura));

        payloads.push({
            IdFactura: firstProduct?.idFactura ?? idFactura,
            ArticulosSeleccionados: selectedArticleIds
        });

        return payloads;
    }, []);

    return {
        IdProyecto: projectData?.idProyecto ?? null,
        IdProyectoD: projectData?.idProyectoD ?? null,
        Facturas: facturas
    };
};

const formatConclusionAmount = (value) => normalizeNumber(value).toFixed(2);

const formatConclusionDate = (value) => {
    if (!hasValue(value)) {
        return null;
    }

    const toConclusionDateString = (date) => (
        Number.isNaN(date.getTime())
            ? null
            : date.toISOString().slice(0, 23)
    );

    if (value instanceof Date) {
        return toConclusionDateString(value);
    }

    const rawValue = `${value}`.trim();
    const dayFirstDateMatch = rawValue.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?)?$/);

    if (dayFirstDateMatch) {
        const [, dayValue, monthValue, yearValue, hourValue = '0', minuteValue = '0', secondValue = '0', millisecondValue = '0'] = dayFirstDateMatch;
        const day = Number(dayValue);
        const month = Number(monthValue);
        const year = Number(yearValue);
        const hour = Number(hourValue);
        const minute = Number(minuteValue);
        const second = Number(secondValue);
        const millisecond = Number(`${millisecondValue}000`.slice(0, 3));
        const parsedDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second, millisecond));

        if (year >= 0 && year < 100) {
            parsedDate.setUTCFullYear(year);
        }

        const isValidDayFirstDate = parsedDate.getUTCFullYear() === year
            && parsedDate.getUTCMonth() === month - 1
            && parsedDate.getUTCDate() === day
            && parsedDate.getUTCHours() === hour
            && parsedDate.getUTCMinutes() === minute
            && parsedDate.getUTCSeconds() === second;

        return isValidDayFirstDate ? toConclusionDateString(parsedDate) : null;
    }

    return toConclusionDateString(new Date(rawValue));
};

const getStoredUserLogin = () => {
    try {
        if (typeof window === 'undefined' || !window?.localStorage) {
            return null;
        }

        const rawUser = window.localStorage.getItem('user');

        if (!rawUser) {
            return null;
        }
        // TODO
        const parsedUser = JSON.parse(rawUser);
        // return 'DMEDINA';
        return parsedUser?.Usuario ?? null;
    } catch (error) {
        return null;
    }
};

const getArticlesWithSeries = (projectCompleteDetail, productsSelected, productSeries, fallbackProjectData = {}) => {
    if (Array.isArray(projectCompleteDetail?.artAndSeries)) {
        return projectCompleteDetail.artAndSeries.flatMap((item) => {
            const articles = Array.isArray(item?.articulos) ? item.articulos : [];
            const allSeries = Array.isArray(item?.series) ? item.series : [];
            const hasIdArticuloOnSeries = allSeries.some((s) => s && typeof s === 'object' && s.idArticulo !== undefined);

            return articles.map((article) => {
                const articleSeries = hasIdArticuloOnSeries
                    ? allSeries.filter((s) => !s?.idArticulo || s.idArticulo === article.idArticulo)
                    : allSeries;

                return {
                    article,
                    series: articleSeries
                        .map((serie) => getSeriesValue(serie))
                        .filter((serie) => hasValue(serie))
                };
            });
        });
    }

    return Object.entries(productsSelected || {}).flatMap(([idFactura, products]) => (
        (products || []).map((product) => {
            const resolvedIdFactura = product?.idFactura ?? idFactura ?? null;
            const productKey = `${resolvedIdFactura}-${product?.idArticulo}`;
            const normalizedSeries = Array.isArray(productSeries?.[productKey])
                ? productSeries[productKey]
                : [];

            return {
                article: {
                    idArticulo: product?.idArticulo ?? null,
                    idProyectoD: product?.idProyectoD ?? fallbackProjectData?.idProyectoD ?? null,
                    articulo: product?.articulo ?? null,
                    moneda: product?.moneda ?? fallbackProjectData?.moneda ?? null,
                    cantidadSeleccionada: product?.cantidadSeleccionada ?? null,
                    precioUnitario: product?.precioUnitario ?? product?.precio ?? null,
                    costoUnitario: product?.costoUnitario ?? null,
                    idFactura: resolvedIdFactura,
                    factura: product?.factura ?? null,
                    cufe: product?.cufe ?? null,
                    idEntradaCompra: product?.idEntradaCompra ?? null
                },
                series: normalizedSeries
                    .map((serie) => getSeriesValue(serie))
                    .filter((serie) => hasValue(serie))
            };
        })
    ));
};

const MONEDA_CODE_TO_INTELISIS = {
    COP: 'Pesos Col',
    USD: 'Dolares',
};

const resolveIntelisisMoneda = (moneda) => {
    if (!moneda) return null;
    return MONEDA_CODE_TO_INTELISIS[`${moneda}`.toUpperCase().trim()] ?? moneda;
};

const buildConcludeProjectPayload = (projectCompleteDetail, productsSelected, productSeries, projectData) => {
    const sourceProjectDetails = projectCompleteDetail?.projectDetails || {};
    const usuario = getStoredUserLogin();
    const idProyecto = sourceProjectDetails?.idProyecto ?? projectData?.idProyecto ?? null;
    const idProyectoD = sourceProjectDetails?.idProyectoD ?? projectData?.idProyectoD ?? null;
    const fechaAlta = sourceProjectDetails?.fechaAlta ?? projectData?.fechaAlta ?? null;
    const moneda = resolveIntelisisMoneda(sourceProjectDetails?.moneda ?? projectData?.moneda ?? null);
    const cliente = sourceProjectDetails?.cliente ?? projectData?.cliente ?? null;
    const referencia = sourceProjectDetails?.referencia ?? projectData?.referencia ?? null;
    const proyecto = sourceProjectDetails?.proyecto ?? 'Stock';
    const agente = sourceProjectDetails?.agente ?? projectData?.agente ?? null;
    const proveedor = sourceProjectDetails?.proveedor ?? projectData?.proveedor ?? null;
    const fechaRecepcion = sourceProjectDetails?.fechaRecepcion ?? null;
    const colFechaFactura = sourceProjectDetails?.colFechaFactura ?? null;
    const sucursalCliente = `${sourceProjectDetails?.sucursalCliente ?? '1'}`;

    const articlesWithSeries = getArticlesWithSeries(
        projectCompleteDetail, productsSelected, productSeries, projectData
    );

    const pedidosArticulos = articlesWithSeries.map(({ article }) => ({
        IdArticulo: article.idArticulo ?? null,
        Articulo: article.articulo ?? null,
        Cantidad: article.cantidadSeleccionada ?? null,
        Precio: article.precioUnitario ?? null
    }));

    const entradasMap = new Map();
    articlesWithSeries
        .filter(({ article }) => article.idEntradaCompra === null || article.idEntradaCompra === undefined)
        .forEach(({ article, series }) => {
            const factura = article.factura ?? '';
            const cufe = article.cufe ?? '';
            const groupKey = `${factura}|||${cufe}`;

            if (!entradasMap.has(groupKey)) {
                entradasMap.set(groupKey, {
                    FechaEmision: fechaAlta,
                    Moneda: moneda,
                    Usuario: usuario,
                    CSCUFE: cufe || null,
                    Proveedor: proveedor,
                    ColNoFactura: factura || null,
                    FechaRecepcion: fechaRecepcion,
                    ColFechaFactura: colFechaFactura,
                    Proyecto: proyecto,
                    articulos: []
                });
            }

            entradasMap.get(groupKey).articulos.push({
                IdArticulo: article.idArticulo ?? null,
                Articulo: article.articulo ?? null,
                Cantidad: article.cantidad ?? null,
                Costo: article.costoUnitario ?? null,
                Series: series
            });
        });

    return {
        IdProyecto: idProyecto,
        IdProyectoD: idProyectoD,
        Entradas: Array.from(entradasMap.values()),
        Pedidos: {
            FechaEmision: fechaAlta,
            Moneda: moneda,
            Cliente: cliente,
            SucursalCliente: sucursalCliente,
            Referencia: referencia,
            Usuario: usuario,
            Proyecto: proyecto,
            Agente: agente,
            articulos: pedidosArticulos
        }
    };
};

const getProductsRequiringIntelisis = (products = []) => (
    (products || []).filter((product) => requiresIntelisisConfig(product))
);

const ProjectOrderWizard = () => {
    const location = useLocation();
    const projectData = location.state?.project;
    const initialStep = location.state?.initialStep ?? 0;

    const [clientInvoices, setClientInvoices] = useState([]);
    const [projectInvoices, setProjectInvoices] = useState([]);
    const [invoicesSelected, setInvoicesSelected] = useState([]);
    const [productsSelected, setProductsSelected] = useState({});
    const [productSeries, setProductSeries] = useState({});
    const [availableInvoicesCatalog, setAvailableInvoicesCatalog] = useState([]);
    const [availableInvoicesLoading, setAvailableInvoicesLoading] = useState(false);
    const [availableInvoicesError, setAvailableInvoicesError] = useState(null);

    const [cuentasIntelisis, setCuentasIntelisis] = useState([]);
    const [conceptosReteFuente, setConceptosReteFuente] = useState([]);
    const [conceptosReteICA, setConceptosReteICA] = useState([]);
    const [intelisisLoading, setIntelisisLoading] = useState(false);
    const [showProductValidationErrors, setShowProductValidationErrors] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'warning' });
    const [currentStep, setCurrentStep] = useState(initialStep);
    const [stepChangeConfirmation, setStepChangeConfirmation] = useState({
        open: false,
        title: '',
        message: ''
    });

    const [loading, setLoading] = useState(true);
    const topInfoArtCacheRef = useRef({});
    const topInfoArtInFlightRef = useRef(new Set());
    const invoicesSelectedRef = useRef([]);
    const allSelectedProductsRef = useRef([]);
    const previewInitialLoadKeyRef = useRef(null);
    const projectCompleteDetailRef = useRef(null);
    const stepChangeConfirmationResolverRef = useRef(null);
    const invoicesCatalogPreparationTokenRef = useRef(initialStep === PROJECT_SELECTION_STEP_INDEX ? 1 : 0);
    const lastExecutedInvoicesCatalogPreparationTokenRef = useRef(0);
    const intelisisStepPreparationTokenRef = useRef(initialStep === PRODUCT_DETAILS_STEP_INDEX ? 1 : 0);
    const lastExecutedIntelisisPreparationTokenRef = useRef(0);
    const classes = useStylesGeneral();

    useEffect(() => {
        invoicesSelectedRef.current = invoicesSelected;
    }, [invoicesSelected]);

    useEffect(() => {
        setCurrentStep(initialStep);
    }, [initialStep]);

    const requestStepChangeConfirmation = useCallback((fromStep, toStep) => {
        if (toStep <= fromStep || !STEP_CHANGE_CONFIRMATION_STEPS.includes(toStep)) {
            return Promise.resolve(true);
        }

        const confirmationCopy = getStepChangeConfirmationCopy(toStep);

        if (stepChangeConfirmationResolverRef.current) {
            stepChangeConfirmationResolverRef.current(false);
        }

        return new Promise((resolve) => {
            stepChangeConfirmationResolverRef.current = resolve;
            setStepChangeConfirmation({
                open: true,
                ...confirmationCopy
            });
        });
    }, []);

    const resolveStepChangeConfirmation = useCallback((confirmed) => {
        if (stepChangeConfirmationResolverRef.current) {
            stepChangeConfirmationResolverRef.current(confirmed);
            stepChangeConfirmationResolverRef.current = null;
        }

        setStepChangeConfirmation({
            open: false,
            title: '',
            message: ''
        });
    }, []);

    const normalizeInvoiceCatalogItem = useCallback((invoiceItem) => {
        const invoice = {
            ...invoiceItem,
            idFactura: invoiceItem?.idFactura,
            factura: invoiceItem?.factura || '',
            fechaActivo: invoiceItem?.fechaActivo || null,
            fechaEmision: invoiceItem?.fechaEmision || null,
            seleccion: invoiceItem?.seleccion ?? null
        };

        const articleDetails = Array.isArray(invoiceItem?.facturas)
            ? invoiceItem.facturas.map((product) => ({
                ...product,
                idFactura: invoice.idFactura,
                factura: product?.factura || invoice.factura,
                descripcion: product?.descripcion || product?.articulo || '',
                cantidad: resolveEffectiveCantidad(product),
                cantidadSeleccionada: resolveEffectiveCantidad(product),
                precioUnitario: product?.precioUnitario,
                seleccion: product?.seleccion ?? 0
            }))
            : [];

        const invoiceDetail = {
            idFactura: invoice.idFactura,
            factura: invoice.factura,
            articleDetails
        };

        return {
            invoice,
            invoiceDetail,
            autocompleteOption: {
                id: invoice.idFactura ?? invoice.factura,
                idFactura: invoice.idFactura,
                factura: invoice.factura,
                label: invoice.factura || 'Sin factura',
                invoice,
                invoiceDetail
            }
        };
    }, []);

    const reloadProjectData = useCallback(async () => {
        if (!projectData?.idProyectoD) {
            setClientInvoices([]);
            setProjectInvoices([]);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const response = await ProjectsService.getProjectDetail(projectData.idProyectoD);

            if (Array.isArray(response) && response.length > 0) {
                const articleDetailsPromises = response.map(async (project, index) => {
                    try {
                        if (project?.factura) {
                            const articleDetail = await ProjectsService.getProjectArticleDetail(project.idFactura, projectData.idProyectoD);
                            return {
                                projectIndex: index,
                                idFactura: project.idFactura,
                                factura: project.factura,
                                articleDetail: articleDetail || []
                            };
                        }
                        return {
                            projectIndex: index,
                            idFactura: project?.idFactura,
                            factura: null,
                            articleDetail: []
                        };
                    } catch (error) {
                        return {
                            projectIndex: index,
                            idFactura: project?.idFactura,
                            factura: project?.factura || null,
                            articleDetail: []
                        };
                    }
                });

                const allArticleDetails = await Promise.all(articleDetailsPromises);

                const invoiceDetails = allArticleDetails.map((detail) => ({
                    idFactura: detail.idFactura,
                    factura: detail.factura,
                    projectIndex: detail.projectIndex,
                    articleDetails: Array.isArray(detail.articleDetail)
                        ? detail.articleDetail.map((product) => ({
                            ...product,
                            idFactura: detail.idFactura,
                            factura: product?.factura || detail.factura || '',
                            cantidad: resolveEffectiveCantidad(product),
                            cantidadSeleccionada: resolveEffectiveCantidad(product),
                        }))
                        : []
                }));

                setProjectInvoices(invoiceDetails);
                setClientInvoices(response);
            } else {
                setProjectInvoices([]);
                setClientInvoices([]);
            }
        } catch (error) {
            setClientInvoices([]);
            setProjectInvoices([]);
        } finally {
            setLoading(false);
        }
    }, [projectData]);

    useEffect(() => {
        if (initialStep >= PRODUCT_SERIES_STEP_INDEX) {
            setLoading(false);
            return;
        }

        reloadProjectData();
    }, [initialStep, reloadProjectData]);

    const loadAvailableInvoicesCatalog = useCallback(async () => {
        try {
            setAvailableInvoicesLoading(true);
            setAvailableInvoicesError(null);

            const response = await ProjectsService.getInvoicesList();
            const normalized = Array.isArray(response)
                ? response.map(normalizeInvoiceCatalogItem)
                : [];

            setAvailableInvoicesCatalog(normalized);

            return {
                success: true
            };
        } catch (error) {
            setAvailableInvoicesCatalog([]);
            setAvailableInvoicesError(error);

            return {
                success: false,
                error
            };
        } finally {
            setAvailableInvoicesLoading(false);
        }
    }, [normalizeInvoiceCatalogItem]);

    useEffect(() => {
        if (currentStep !== PROJECT_SELECTION_STEP_INDEX) {
            return;
        }

        const currentPreparationToken = invoicesCatalogPreparationTokenRef.current;

        if (!currentPreparationToken || lastExecutedInvoicesCatalogPreparationTokenRef.current === currentPreparationToken) {
            return;
        }

        lastExecutedInvoicesCatalogPreparationTokenRef.current = currentPreparationToken;
        loadAvailableInvoicesCatalog();
    }, [currentStep, loadAvailableInvoicesCatalog]);

    const cuentasIntelisisOptions = useMemo(() => normalizeIntelisisOptions(cuentasIntelisis), [cuentasIntelisis]);
    const conceptosReteFuenteOptions = useMemo(() => normalizeIntelisisOptions(conceptosReteFuente), [conceptosReteFuente]);
    const conceptosReteICAOptions = useMemo(() => normalizeIntelisisOptions(conceptosReteICA), [conceptosReteICA]);

    const productDetailsValidation = useMemo(() => {
        const invalidProducts = [];
        const invalidProductsMap = {};
        let requiresIntelisisCount = 0;

        Object.entries(productsSelected || {}).forEach(([idFactura, products]) => {
            (products || []).forEach((product) => {
                if (!requiresIntelisisConfig(product)) {
                    return;
                }

                requiresIntelisisCount += 1;

                const productKey = `${idFactura}-${product.idArticulo}`;
                const missingFields = [];

                REQUIRED_INTELISIS_PRODUCT_FIELDS.forEach(({ field, valueKeys }) => {
                    const hasFieldValue = (valueKeys || []).some((valueKey) => hasValue(product?.[valueKey]));

                    if (!hasFieldValue) {
                        missingFields.push(field);
                    }
                });

                if (!isValidArticleCode(product?.articulo)) {
                    missingFields.push('articulo');
                }

                if (!isWithinMaxLength(product?.articulo, MAX_ARTICLE_CODE_LENGTH)) {
                    missingFields.push('articulo');
                }

                if (!isWithinMaxLength(product?.nombreCorto ?? product?.NombreCorto ?? product?.nombrecorto ?? product?.articulo, MAX_ARTICLE_CODE_LENGTH)) {
                    missingFields.push('nombreCorto');
                }

                if (!missingFields.includes('impuesto1') && !isValidNumericValue(product?.impuesto1 ?? product?.Impuesto1)) {
                    missingFields.push('impuesto1');
                }

                REQUIRED_INTELISIS_ACCOUNT_FIELDS.forEach(({ field, valueKey }) => {
                    if (!hasValue(product?.[valueKey])) {
                        missingFields.push(field);
                    }
                });

                getDuplicateIntelisisAccountFields(product).forEach((field) => {
                    missingFields.push(field);
                });

                if (!hasValue(product.intelisis_reteFuente)) {
                    missingFields.push('reteFuente');
                }

                if (!hasValue(product.intelisis_reteICA)) {
                    missingFields.push('reteICA');
                }

                if (missingFields.length > 0) {
                    const invalidProduct = {
                        productKey,
                        idFactura,
                        idArticulo: product.idArticulo,
                        articulo: product.articulo,
                        missingFields
                    };

                    invalidProducts.push(invalidProduct);
                    invalidProductsMap[productKey] = invalidProduct;
                }
            });
        });

        const catalogsReady = cuentasIntelisisOptions.length > 0
            && conceptosReteFuenteOptions.length > 0
            && conceptosReteICAOptions.length > 0;

        return {
            catalogsReady,
            invalidProducts,
            invalidProductsMap,
            invalidCount: invalidProducts.length,
            requiresIntelisisCount
        };
    }, [
        cuentasIntelisisOptions,
        conceptosReteFuenteOptions,
        conceptosReteICAOptions,
        productsSelected
    ]);

    const allSelectedProducts = useMemo(() => (
        Object.entries(productsSelected || {}).flatMap(([idFactura, products]) =>
            (products || []).map((product) => ({
                ...product,
                idFactura: product?.idFactura ?? idFactura
            }))
        )
    ), [productsSelected]);

    useEffect(() => {
        allSelectedProductsRef.current = allSelectedProducts;
    }, [allSelectedProducts]);

    const applyTopInfoArtToProducts = useCallback((lookupKey, topInfoArtData) => {
        if (!lookupKey || !topInfoArtData) {
            return;
        }

        setProductsSelected((prevState) => {
            let hasChanges = false;
            const nextState = { ...prevState };

            Object.entries(prevState || {}).forEach(([idFactura, products]) => {
                let invoiceHasChanges = false;
                const nextProducts = (products || []).map((product) => {
                    if (!requiresIntelisisConfig(product)) {
                        return product;
                    }

                    if (buildTopInfoArtKey(product?.fabricante, product?.categoria) !== lookupKey) {
                        return product;
                    }

                    const nextProduct = mergeTopInfoArtIntoProduct(product, topInfoArtData);

                    if (nextProduct !== product) {
                        invoiceHasChanges = true;
                        hasChanges = true;
                    }

                    return nextProduct;
                });

                if (invoiceHasChanges) {
                    nextState[idFactura] = nextProducts;
                }
            });

            return hasChanges ? nextState : prevState;
        });
    }, []);

    const loadIntelisisCatalogs = useCallback(async () => {
        const catalogsAlreadyLoaded = cuentasIntelisisOptions.length > 0
            && conceptosReteFuenteOptions.length > 0
            && conceptosReteICAOptions.length > 0;

        if (catalogsAlreadyLoaded) {
            return {
                success: true,
                skipped: true
            };
        }

        try {
            setIntelisisLoading(true);

            const [cuentasResponse, reteFuenteResponse, reteICAResponse] = await Promise.all([
                CatalogsService.obtenerCta(),
                CatalogsService.conceptosReteFuente(),
                CatalogsService.conceptosReteICA()
            ]);

            setCuentasIntelisis(cuentasResponse || []);
            setConceptosReteFuente(reteFuenteResponse || []);
            setConceptosReteICA(reteICAResponse || []);

            return {
                success: true
            };
        } catch (error) {
            setCuentasIntelisis([]);
            setConceptosReteFuente([]);
            setConceptosReteICA([]);

            return {
                success: false,
                error
            };
        } finally {
            setIntelisisLoading(false);
        }
    }, [conceptosReteFuenteOptions.length, conceptosReteICAOptions.length, cuentasIntelisisOptions.length]);

    const prefillTopInfoArtForStepProducts = useCallback(async () => {
        const snapshotProducts = allSelectedProductsRef.current || [];
        const productsRequiringIntelisis = snapshotProducts.filter((product) => (
            requiresIntelisisConfig(product)
            && hasValue(product?.fabricante)
            && hasValue(product?.categoria)
            && product?.topInfoArtLoaded !== true
        ));

        const pendingRequests = new Map();

        productsRequiringIntelisis.forEach((product) => {
            const lookupKey = buildTopInfoArtKey(product.fabricante, product.categoria);

            if (!lookupKey) {
                return;
            }

            const cachedTopInfoArt = topInfoArtCacheRef.current[lookupKey];
            if (cachedTopInfoArt) {
                applyTopInfoArtToProducts(lookupKey, cachedTopInfoArt);
                return;
            }

            if (topInfoArtInFlightRef.current.has(lookupKey) || pendingRequests.has(lookupKey)) {
                return;
            }

            pendingRequests.set(lookupKey, {
                fabricante: product.fabricante,
                categoria: product.categoria
            });
        });

        if (pendingRequests.size === 0) {
            return;
        }

        await Promise.all(Array.from(pendingRequests.entries()).map(async ([lookupKey, { fabricante, categoria }]) => {
            topInfoArtInFlightRef.current.add(lookupKey);

            try {
                const response = await ProjectsService.getTopInfoArt(fabricante, categoria);
                const topInfoArtData = normalizeTopInfoArtResponse(response);

                topInfoArtCacheRef.current[lookupKey] = topInfoArtData;
                applyTopInfoArtToProducts(lookupKey, topInfoArtData);
            } catch (error) {
            } finally {
                topInfoArtInFlightRef.current.delete(lookupKey);
            }
        }));
    }, [applyTopInfoArtToProducts]);

    const prepareIntelisisStepData = useCallback(() => {
        const currentPreparationToken = intelisisStepPreparationTokenRef.current;

        if (!currentPreparationToken || lastExecutedIntelisisPreparationTokenRef.current === currentPreparationToken) {
            return undefined;
        }

        lastExecutedIntelisisPreparationTokenRef.current = currentPreparationToken;
        let isCancelled = false;

        const prepareIntelisisStep = async () => {
            const catalogsResult = await loadIntelisisCatalogs();

            if (isCancelled || !catalogsResult?.success) {
                return;
            }

            await prefillTopInfoArtForStepProducts();
        };

        prepareIntelisisStep();

        return () => {
            isCancelled = true;
        };
    }, [loadIntelisisCatalogs, prefillTopInfoArtForStepProducts]);

    useEffect(() => {
        if (currentStep !== PRODUCT_DETAILS_STEP_INDEX || loading) {
            return;
        }

        const cleanup = prepareIntelisisStepData();

        return cleanup;
    }, [currentStep, loading, prepareIntelisisStepData]);

    const prepareRegistrarArtPayloads = useCallback((products, currentProjectData) => (
        (products || []).map((product) => {
            const { payload, missingFields } = buildRegistrarArtPayload(product, currentProjectData);

            return {
                product,
                articulo: product?.articulo,
                idArticulo: product?.idArticulo,
                idFactura: product?.idFactura,
                payload,
                missingFields
            };
        })
    ), []);

    const loadProjectCompleteDetailForPreview = useCallback(async () => {
        if (!projectData?.idProyecto) {
            return {
                success: false,
                reason: 'project_complete_detail_missing_project',
                message: 'No se pudo cargar el resumen del proyecto. Verifica que el proyecto este correctamente configurado.'
            };
        }

        try {
            const response = await ProjectsService.getProjectCompleteDetail(projectData.idProyecto);
            projectCompleteDetailRef.current = response || null;
            const normalizedData = normalizeProjectCompleteDetail(
                response,
                invoicesSelectedRef.current,
                projectData
            );

            setInvoicesSelected(normalizedData.invoices);
            setProductsSelected(normalizedData.productsByInvoice);
            setProductSeries(normalizedData.productSeriesMap);

            return {
                success: true
            };
        } catch (error) {
            return {
                success: false,
                reason: 'project_complete_detail_failed',
                message: 'Ocurrio un error al cargar el resumen del proyecto. Intenta de nuevo.',
                error
            };
        }
    }, [projectData]);

    const projectId = projectData?.idProyecto;

    useEffect(() => {
        if (
            ![PROJECT_ORDER_PREVIEW_STEP_INDEX, PROJECT_ORDER_GENERATED_STEP_INDEX].includes(initialStep)
            || !projectId
        ) {
            return;
        }

        const loadKey = `${projectId}-${initialStep}`;
        if (previewInitialLoadKeyRef.current === loadKey) {
            return;
        }

        previewInitialLoadKeyRef.current = loadKey;
        loadProjectCompleteDetailForPreview().then((result) => {
            if (!result?.success) {
                setSnackbar({
                    open: true,
                    message: result?.message || 'Ocurrio un error al cargar el resumen del proyecto. Intenta de nuevo.',
                    severity: 'error'
                });
            }
        });
    }, [initialStep, loadProjectCompleteDetailForPreview, projectId]);

    const concludeCurrentProject = useCallback(async () => {
        const conclusionPayload = buildConcludeProjectPayload(
            projectCompleteDetailRef.current,
            productsSelected,
            productSeries,
            projectData
        );
        const conclusionArticulos = Array.isArray(conclusionPayload?.Pedidos?.articulos)
            ? conclusionPayload.Pedidos.articulos
            : [];

        if (conclusionArticulos.length === 0) {
            return {
                success: false,
                reason: 'conclude_project_payload_empty',
                message: 'No hay articulos registrados para finalizar. Verifica la configuracion del proyecto.',
                conclusionPayload
            };
        }

        const invalidProjectPayload = !hasValue(conclusionPayload?.IdProyecto)
            || !hasValue(conclusionPayload?.IdProyectoD);
        const invalidArticles = conclusionArticulos.filter((article) => (
            !hasValue(article?.IdArticulo)
            || !hasValue(article?.Articulo)
            || !hasValue(article?.Cantidad)
            || !hasValue(article?.Precio)
        ));
        const invalidSeries = (conclusionPayload?.Entradas || []).flatMap((entrada) =>
            (entrada?.articulos || []).filter((art) => !Array.isArray(art?.Series) || art.Series.length === 0)
        );

        if (invalidProjectPayload || invalidArticles.length > 0 || invalidSeries.length > 0) {
            return {
                success: false,
                reason: 'conclude_project_payload_invalid',
                message: 'No se pudo finalizar el proyecto. Verifica que todas las facturas, articulos y series esten completos.',
                conclusionPayload
            };
        }

        try {
            await ProjectsService.concludeProject(conclusionPayload);

            return {
                success: true,
                conclusionPayload
            };
        } catch (error) {
            return {
                success: false,
                reason: 'conclude_project_failed',
                message: 'Ocurrio un error al finalizar el proyecto. Intenta de nuevo.',
                conclusionPayload,
                error
            };
        }
    }, [productSeries, productsSelected, projectData]);

    const confirmSelectedProducts = useCallback(async () => {
        const confirmationPayload = buildConfirmProjectPayload(productsSelected, projectData);
        const confirmationFacturas = confirmationPayload?.Facturas || [];

        if (confirmationFacturas.length === 0) {
            return {
                success: false,
                reason: 'confirm_project_payload_empty',
                message: 'No hay facturas con articulos seleccionados para confirmar.',
                confirmationPayload
            };
        }

        const invalidProjectPayload = !hasValue(confirmationPayload?.IdProyecto)
            || !hasValue(confirmationPayload?.IdProyectoD);
        const invalidFacturas = confirmationFacturas.filter((payload) => (
            !hasValue(payload?.IdFactura)
            || !Array.isArray(payload?.ArticulosSeleccionados)
            || payload.ArticulosSeleccionados.length === 0
        ));

        if (invalidProjectPayload || invalidFacturas.length > 0) {
            return {
                success: false,
                reason: 'confirm_project_payload_invalid',
                message: 'No se pudo confirmar la orden. Verifica que todas las facturas tengan articulos seleccionados.',
                confirmationPayload
            };
        }

        try {
            await ProjectsService.confirmProject(confirmationPayload);

            return {
                success: true,
                confirmationPayload
            };
        } catch (error) {
            return {
                success: false,
                reason: 'confirm_project_failed',
                message: 'Ocurrio un error al confirmar la orden. Intenta de nuevo.',
                confirmationPayload,
                error
            };
        }
    }, [productsSelected, projectData]);

    const prepareRegisteredProducts = useCallback(async (productsRequiringIntelisis, currentProjectData) => {
        const registrarArtPayloads = prepareRegistrarArtPayloads(productsRequiringIntelisis, currentProjectData);

        if (registrarArtPayloads.length === 0) {
            return {
                success: true,
                registeredProducts: [],
                registrarArtPayloads: []
            };
        }


        if (registrarArtPayloads.some((item) => item.missingFields.length > 0)) {


            return {
                success: false,
                registeredProducts: [],
                registrarArtPayloads,
                reason: 'registrar_art_payload_invalid',
                error: new Error('Hay articulos con campos faltantes para registrarART')
            };
        }

        try {
            await Promise.all(
                registrarArtPayloads.map((item) => CatalogsService.registrarART(item.payload))
            );

            const registeredProducts = registrarArtPayloads.map((item) => item.product);

            return {
                success: true,
                registeredProducts,
                registrarArtPayloads
            };
        } catch (error) {
            return {
                success: false,
                registeredProducts: [],
                registrarArtPayloads,
                error
            };
        }
    }, [prepareRegistrarArtPayloads]);

    const buildFinalInsertPricesPayload = useCallback((products, registeredProducts) => {
        const baseProducts = (products || []).filter((product) => !requiresIntelisisConfig(product));
        const productsForPricing = [...baseProducts, ...(registeredProducts || [])];

        return productsForPricing.map((product) => buildInsertPricePayload(product, projectData));
    }, [projectData]);

    const persistProductDetailsStep = useCallback(async () => {
        const productsRequiringIntelisis = getProductsRequiringIntelisis(allSelectedProducts);
        const shouldRegisterProducts = productsRequiringIntelisis.length > 0;

        let registeredProducts = [];

        if (shouldRegisterProducts) {
            const registerResult = await prepareRegisteredProducts(productsRequiringIntelisis, projectData);

            if (!registerResult.success) {
                return {
                    success: false,
                    reason: registerResult.reason || 'registrar_art_failed',
                    severity: 'error',
                    message: registerResult.reason === 'registrar_art_payload_invalid'
                        ? 'Completa todos los campos requeridos antes de continuar.'
                        : 'Ocurrio un error al registrar los articulos. No se procesaron los precios. Intenta de nuevo.'
                };
            }

            registeredProducts = registerResult.registeredProducts;
        }

        const insertPricesPayload = buildFinalInsertPricesPayload(allSelectedProducts, registeredProducts);
        const invalidPricePayloads = insertPricesPayload.filter((price) => !hasValue(price.IdArticulo) || !hasValue(price.Precio));

        if (invalidPricePayloads.length > 0) {
            return {
                success: false,
                reason: 'insert_prices_payload_invalid',
                severity: 'error',
                message: 'No se pudieron procesar los precios. Hay articulos con informacion incompleta.'
            };
        }

        if (insertPricesPayload.length === 0) {
            return {
                success: true,
                shouldShowSuccess: false
            };
        }

        try {
            await ProjectsService.insertPrices(insertPricesPayload);

            return {
                success: true,
                shouldShowSuccess: true,
                successMessage: shouldRegisterProducts
                    ? 'Articulos configurados y precios registrados correctamente.'
                    : 'Precios registrados correctamente.'
            };
        } catch (error) {
            return {
                success: false,
                reason: 'insert_prices_failed',
                severity: 'error',
                message: 'Ocurrio un error al procesar los precios. Intenta de nuevo.',
                error
            };
        }
    }, [allSelectedProducts, buildFinalInsertPricesPayload, prepareRegisteredProducts, projectData]);

    const persistProductSeriesStep = useCallback(async () => {
        const seriesValidation = getProductSeriesValidationSummary(productsSelected, productSeries);

        if (seriesValidation.invalidCount > 0) {
            return {
                success: false,
                reason: 'product_series_incomplete',
                severity: 'warning',
                invalidCount: seriesValidation.invalidCount,
                message: `Asigna las series faltantes de ${seriesValidation.invalidCount} producto${seriesValidation.invalidCount === 1 ? '' : 's'} antes de continuar.`
            };
        }

        const confirmationResult = await confirmSelectedProducts();

        if (!confirmationResult.success) {
            return {
                success: false,
                reason: confirmationResult.reason || 'confirm_project_failed',
                severity: 'error',
                message: confirmationResult.message || 'No se pudo confirmar el proyecto.'
            };
        }

        const previewDetailResult = await loadProjectCompleteDetailForPreview();

        if (!previewDetailResult.success) {
            return {
                success: false,
                reason: previewDetailResult.reason || 'project_complete_detail_failed',
                severity: 'error',
                message: previewDetailResult.message || 'Ocurrio un error al cargar el resumen del proyecto. Intenta de nuevo.'
            };
        }

        return {
            success: true,
            shouldShowSuccess: true,
            successMessage: 'Series asignadas y orden confirmada correctamente.'
        };
    }, [confirmSelectedProducts, loadProjectCompleteDetailForPreview, productSeries, productsSelected]);

    const persistProjectConclusionStep = useCallback(async () => {
        const seriesValidation = getProductSeriesValidationSummary(productsSelected, productSeries);

        if (seriesValidation.invalidCount > 0) {
            return {
                success: false,
                reason: 'product_series_incomplete',
                severity: 'warning',
                invalidCount: seriesValidation.invalidCount,
                message: `Asigna las series faltantes de ${seriesValidation.invalidCount} producto${seriesValidation.invalidCount === 1 ? '' : 's'} antes de continuar.`
            };
        }

        const conclusionResult = await concludeCurrentProject();

        if (!conclusionResult.success) {
            return {
                success: false,
                reason: conclusionResult.reason || 'conclude_project_failed',
                severity: 'error',
                message: conclusionResult.message || 'No se pudo concluir el proyecto.'
            };
        }

        const previewDetailResult = await loadProjectCompleteDetailForPreview();

        if (!previewDetailResult.success) {
            return {
                success: false,
                reason: previewDetailResult.reason || 'project_complete_detail_failed',
                severity: 'error',
                message: previewDetailResult.message || 'Ocurrio un error al cargar el resumen final. Intenta de nuevo.'
            };
        }

        return {
            success: true,
            shouldShowSuccess: true,
            successMessage: 'Proyecto finalizado. El resumen de la orden ha sido generado correctamente.'
        };
    }, [concludeCurrentProject, loadProjectCompleteDetailForPreview, productSeries, productsSelected]);

    const handleBeforeStepChange = useCallback(async (fromStep, toStep) => {
        if (fromStep >= PRODUCT_SERIES_STEP_INDEX && toStep < fromStep) {
            return {
                allowed: false,
                reason: 'back_navigation_locked',
                severity: 'warning',
                message: 'No es posible regresar una vez iniciada la asignacion de series.'
            };
        }

        if (toStep <= fromStep) {
            return true;
        }

        if (fromStep === PROJECT_SELECTION_STEP_INDEX && toStep > PROJECT_SELECTION_STEP_INDEX && allSelectedProducts.length === 0) {
            return {
                allowed: false,
                reason: 'project_selection_required',
                severity: 'warning',
                message: 'Selecciona al menos un articulo para continuar.'
            };
        }

        if (fromStep <= PRODUCT_DETAILS_STEP_INDEX && toStep > PRODUCT_DETAILS_STEP_INDEX) {
            if (productDetailsValidation.requiresIntelisisCount > 0 && !productDetailsValidation.catalogsReady) {
                setShowProductValidationErrors(true);
                return {
                    allowed: false,
                    reason: 'intelisis_catalogs_unavailable',
                    invalidCount: productDetailsValidation.requiresIntelisisCount
                };
            }

            if (productDetailsValidation.invalidCount > 0) {
                setShowProductValidationErrors(true);
                return {
                    allowed: false,
                    reason: 'intelisis_fields_required',
                    invalidCount: productDetailsValidation.invalidCount
                };
            }

            setShowProductValidationErrors(false);
            const confirmedStepChange = await requestStepChangeConfirmation(fromStep, toStep);

            if (!confirmedStepChange) {
                return {
                    allowed: false,
                    reason: 'step_change_cancelled',
                    severity: 'info',
                    message: 'Operacion cancelada.'
                };
            }

            return persistProductDetailsStep().then((result) => {
                if (!result?.success) {
                    return {
                        allowed: false,
                        reason: result?.reason || 'persist_product_details_failed',
                        severity: result?.severity || 'error',
                        message: result?.message || 'Ocurrio un error al guardar la informacion. Intenta de nuevo.'
                    };
                }

                if (result?.shouldShowSuccess && result?.successMessage) {
                    setSnackbar({
                        open: true,
                        message: result.successMessage,
                        severity: 'success'
                    });
                }

                if (toStep > PRODUCT_SERIES_STEP_INDEX) {
                    return persistProductSeriesStep().then((seriesResult) => {
                        if (!seriesResult?.success) {
                            return {
                                allowed: false,
                                reason: seriesResult?.reason || 'persist_product_series_failed',
                                severity: seriesResult?.severity || 'error',
                                invalidCount: seriesResult?.invalidCount,
                                message: seriesResult?.message || 'Ocurrio un error al guardar las series. Intenta de nuevo.'
                            };
                        }

                        if (seriesResult?.shouldShowSuccess && seriesResult?.successMessage) {
                            setSnackbar({
                                open: true,
                                message: seriesResult.successMessage,
                                severity: 'success'
                            });
                        }

                        if (toStep > PROJECT_ORDER_PREVIEW_STEP_INDEX) {
                            return persistProjectConclusionStep().then((conclusionResult) => {
                                if (conclusionResult?.success) {
                                    if (conclusionResult?.shouldShowSuccess && conclusionResult?.successMessage) {
                                        setSnackbar({
                                            open: true,
                                            message: conclusionResult.successMessage,
                                            severity: 'success'
                                        });
                                    }

                                    return true;
                                }

                                return {
                                    allowed: false,
                                    reason: conclusionResult?.reason || 'persist_project_conclusion_failed',
                                    severity: conclusionResult?.severity || 'error',
                                    invalidCount: conclusionResult?.invalidCount,
                                    message: conclusionResult?.message || 'No se pudo concluir el proyecto.'
                                };
                            });
                        }

                        return true;
                    });
                }

                return true;
            });
        }

        if (fromStep <= PRODUCT_SERIES_STEP_INDEX && toStep > PRODUCT_SERIES_STEP_INDEX) {
            const confirmedStepChange = await requestStepChangeConfirmation(fromStep, toStep);

            if (!confirmedStepChange) {
                return {
                    allowed: false,
                    reason: 'step_change_cancelled',
                    severity: 'info',
                    message: 'Operacion cancelada.'
                };
            }

            return persistProductSeriesStep().then((result) => {
                if (!result?.success) {
                    return {
                        allowed: false,
                        reason: result?.reason || 'persist_product_series_failed',
                        severity: result?.severity || 'error',
                        invalidCount: result?.invalidCount,
                        message: result?.message || 'Ocurrio un error al guardar las series. Intenta de nuevo.'
                    };
                }

                if (result?.shouldShowSuccess && result?.successMessage) {
                    setSnackbar({
                        open: true,
                        message: result.successMessage,
                        severity: 'success'
                    });
                }

                if (toStep > PROJECT_ORDER_PREVIEW_STEP_INDEX) {
                    return persistProjectConclusionStep().then((conclusionResult) => {
                        if (conclusionResult?.success) {
                            if (conclusionResult?.shouldShowSuccess && conclusionResult?.successMessage) {
                                setSnackbar({
                                    open: true,
                                    message: conclusionResult.successMessage,
                                    severity: 'success'
                                });
                            }

                            return true;
                        }

                        return {
                            allowed: false,
                            reason: conclusionResult?.reason || 'persist_project_conclusion_failed',
                            severity: conclusionResult?.severity || 'error',
                            invalidCount: conclusionResult?.invalidCount,
                            message: conclusionResult?.message || 'No se pudo concluir el proyecto.'
                        };
                    });
                }

                return true;
            });
        }

        if (fromStep === PROJECT_ORDER_PREVIEW_STEP_INDEX && toStep === PROJECT_ORDER_GENERATED_STEP_INDEX) {
            const confirmedStepChange = await requestStepChangeConfirmation(fromStep, toStep);

            if (!confirmedStepChange) {
                return {
                    allowed: false,
                    reason: 'step_change_cancelled',
                    severity: 'info',
                    message: 'Operacion cancelada.'
                };
            }

            return persistProjectConclusionStep().then((result) => {
                if (result?.success) {
                    if (result?.shouldShowSuccess && result?.successMessage) {
                        setSnackbar({
                            open: true,
                            message: result.successMessage,
                            severity: 'success'
                        });
                    }

                    return true;
                }

                return {
                    allowed: false,
                    reason: result?.reason || 'persist_project_conclusion_failed',
                    severity: result?.severity || 'error',
                    invalidCount: result?.invalidCount,
                    message: result?.message || 'No se pudo concluir el proyecto.'
                };
            });
        }

        const confirmedStepChange = await requestStepChangeConfirmation(fromStep, toStep);

        if (!confirmedStepChange) {
            return {
                allowed: false,
                reason: 'step_change_cancelled',
                severity: 'info',
                message: 'Cambio de paso cancelado.'
            };
        }

        return true;
    }, [allSelectedProducts.length, persistProductDetailsStep, persistProductSeriesStep, persistProjectConclusionStep, productDetailsValidation, requestStepChangeConfirmation]);

    const handleBlockedStepChange = useCallback((payload = {}) => {
        const invalidCount = payload.invalidCount || productDetailsValidation.invalidCount;
        const severity = payload.reason === 'intelisis_catalogs_unavailable'
            ? 'error'
            : payload.reason === 'intelisis_fields_required'
                ? 'warning'
                : (payload.severity || 'error');
        const message = payload.reason === 'intelisis_catalogs_unavailable'
            ? 'Los catalogos de configuracion aun no estan disponibles. Intenta de nuevo en un momento.'
            : payload.reason === 'intelisis_fields_required'
                ? `Completa los campos requeridos en ${invalidCount} producto${invalidCount === 1 ? '' : 's'} antes de continuar.`
                : payload.reason === 'product_series_incomplete'
                    ? `Asigna las series faltantes de ${invalidCount} producto${invalidCount === 1 ? '' : 's'} antes de continuar.`
                    : payload.reason === 'back_navigation_locked'
                        ? 'No es posible regresar una vez iniciada la asignacion de series.'
                        : (payload.message || 'No se pudo completar la accion solicitada.');

        setSnackbar({
            open: true,
            message,
            severity
        });
    }, [productDetailsValidation.invalidCount]);

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setSnackbar({ open: false, message: '', severity: 'warning' });
    };

    return (
        <div className={`${classes.root}`} style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '20px' }}>
            <TextTitleCS title={projectData ? `${projectData.nombreProyecto}`.toUpperCase() : "PROYECTO"} />

            <StepperCS
                steps={5}
                initialStep={initialStep}
                buttonsPosition="bottom"
                labelsteps={["Seleccion Facturas/Productos", "Detalle de productos", "Configuracion de series", "Resumen de Orden", "Resumen de orden generada"]}
                componente={[
                    {
                        Panel: <ProjectDetails
                            projectData={projectData}
                            clientInvoices={clientInvoices}
                            projectInvoices={projectInvoices}
                            invoicesSelected={invoicesSelected}
                            setInvoicesSelected={setInvoicesSelected}
                            productsSelected={productsSelected}
                            setProductsSelected={setProductsSelected}
                            productSeries={productSeries}
                            setProductSeries={setProductSeries}
                            availableInvoicesCatalog={availableInvoicesCatalog}
                            availableInvoicesLoading={availableInvoicesLoading}
                            availableInvoicesError={availableInvoicesError}
                            reloadProjectData={reloadProjectData}
                        />
                    },
                    {
                        Panel: <ProductDetails
                            invoicesSelected={invoicesSelected}
                            productsSelected={productsSelected}
                            setProductsSelected={setProductsSelected}
                            projectData={projectData}
                            cuentasIntelisis={cuentasIntelisis}
                            conceptosReteFuente={conceptosReteFuente}
                            conceptosReteICA={conceptosReteICA}
                            intelisisLoading={intelisisLoading}
                            validationSummary={productDetailsValidation}
                            showValidationErrors={showProductValidationErrors}
                        />
                    },
                    {
                        Panel: <ProductSeries
                            invoicesSelected={invoicesSelected}
                            productsSelected={productsSelected}
                            setProductsSelected={setProductsSelected}
                            productSeries={productSeries}
                            setProductSeries={setProductSeries}
                            projectData={projectData}
                            cuentasIntelisis={cuentasIntelisis}
                            conceptosReteFuente={conceptosReteFuente}
                            conceptosReteICA={conceptosReteICA}
                            intelisisLoading={intelisisLoading}
                        />
                    },
                    {
                        Panel: <ProjectOrderPreview
                            projectData={projectData}
                            invoicesSelected={invoicesSelected}
                            productsSelected={productsSelected}
                            productSeries={productSeries}
                            cuentasIntelisis={cuentasIntelisis}
                            conceptosReteFuente={conceptosReteFuente}
                            conceptosReteICA={conceptosReteICA}
                            title="RESUMEN DE ORDEN DE COMPRA"
                        />
                    },
                    {
                        Panel: <ProjectOrderPreview
                            projectData={projectData}
                            invoicesSelected={invoicesSelected}
                            productsSelected={productsSelected}
                            productSeries={productSeries}
                            cuentasIntelisis={cuentasIntelisis}
                            conceptosReteFuente={conceptosReteFuente}
                            conceptosReteICA={conceptosReteICA}
                            title="RESUMEN DE ORDEN EN INTELISIS"
                        />
                    }
                ]}
                beforeStepChange={handleBeforeStepChange}
                onBlockedStepChange={handleBlockedStepChange}
                handleSubmit={({ toStep }) => {
                    setCurrentStep(toStep);
                    if (toStep === PROJECT_SELECTION_STEP_INDEX) {
                        invoicesCatalogPreparationTokenRef.current += 1;
                    }
                    if (toStep === PRODUCT_DETAILS_STEP_INDEX) {
                        intelisisStepPreparationTokenRef.current += 1;
                        if (!loading) {
                            prepareIntelisisStepData();
                        }
                    }
                }}
            />

            <ModalCS
                show={stepChangeConfirmation.open}
                title={stepChangeConfirmation.title}
                size="md"
                handleClose={() => resolveStepChangeConfirmation(false)}
            >
                <div style={{ padding: '10px 5px' }}>
                    <p style={{ marginBottom: '20px' }}>
                        {stepChangeConfirmation.message}
                    </p>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <Button
                            variant="outlined"
                            onClick={() => resolveStepChangeConfirmation(false)}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => resolveStepChangeConfirmation(true)}
                        >
                            Continuar
                        </Button>
                    </div>
                </div>
            </ModalCS>

            <Snackbar
                className="app-toast"
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default ProjectOrderWizard;
